import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type HistoryEvent, type HistoryPage } from '@/entities/database';
import { useViewer } from '@/entities/viewer';
import { formatDateTime } from '@/shared/lib/datetime';
import { Button, ErrorState, LoadingState } from '@/shared/ui';

import { describe, type HistoryLine } from './describe';
import { groupByOperation, type HistoryGroup, mainEvent } from './group';
import styles from './History.module.css';
import { HistoryDetailsDialog } from './HistoryDetailsDialog';
import { HistoryValueDialog } from './HistoryValueDialog';

const CUT = 160;

interface Props {
  query: UseInfiniteQueryResult<InfiniteData<HistoryPage, string | null>>;
  /** Filters are set: an empty result reads "no matches" instead of "nothing recorded". */
  filtered?: boolean;
  /** Needed to read the stored changes of bulk entries. */
  databaseId: string;
  /** The list shows one record's History: bulk entries open narrowed to it. */
  recordId?: string | null;
}

/** A timeline of History entries: loading, error and empty states, groups, "older" paging. */
export function HistoryList({ query, filtered = false, databaseId, recordId = null }: Props) {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const timeZone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const [detail, setDetail] = useState<{ title: string; line: HistoryLine } | null>(null);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [bulk, setBulk] = useState<{ event: HistoryEvent; text: string } | null>(null);

  const pages = query.data?.pages ?? [];
  const users = Object.assign({}, ...pages.map((page) => page.users)) as Record<string, string>;
  const events = pages.flatMap((page) => page.items);
  const context = { t, locale: i18n.language, timeZone, users };

  const entry = (event: HistoryEvent, nested = false) => {
    const { text, lines } = describe(event, context);
    return (
      <li
        key={event.id}
        className={nested ? styles.nested : styles.item}
        data-testid="history-item"
        data-event={event.event_type}
      >
        <p className={styles.text}>{text}</p>
        {lines.map((line, index) => (
          <Line
            key={index}
            line={line}
            onDetail={() => {
              setDetail({ title: text, line });
            }}
          />
        ))}
        {chunked(event) && (
          <button
            type="button"
            className={styles.more}
            onClick={() => {
              setBulk({ event, text });
            }}
          >
            {recordId
              ? t('databases.history.showRecordChanges')
              : t('databases.history.showAll', { count: totalOf(event) })}
          </button>
        )}
        <time className={styles.time} dateTime={event.created_at}>
          {formatDateTime(event.created_at, i18n.language, timeZone)}
        </time>
      </li>
    );
  };

  const group = (item: HistoryGroup) => {
    if (item.events.length === 1 && item.events[0]) return entry(item.events[0]);
    const main = mainEvent(item);
    const first = item.events[0];
    if (!first) return null;
    const expanded = open.has(item.key);
    const heading = main
      ? describe(main, context).text
      : t('databases.history.group', {
          actor: first.actor.name ?? t('databases.history.unknownActor'),
          count: item.events.length,
        });
    return (
      <li key={item.key} className={styles.item} data-testid="history-group">
        <p className={styles.text}>{heading}</p>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={expanded}
          onClick={() => {
            setOpen((current) => {
              const next = new Set(current);
              if (next.has(item.key)) next.delete(item.key);
              else next.add(item.key);
              return next;
            });
          }}
        >
          {expanded ? t('databases.history.hideGroup') : t('databases.history.showGroup')} (
          {item.events.length})
        </button>
        <time className={styles.time} dateTime={first.created_at}>
          {formatDateTime(first.created_at, i18n.language, timeZone)}
        </time>
        {expanded && (
          <ol className={styles.nestedList}>{item.events.map((event) => entry(event, true))}</ol>
        )}
      </li>
    );
  };

  return (
    <div className={styles.body}>
      {query.isPending && <LoadingState />}
      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {query.data && events.length === 0 && (
        <p className={styles.empty}>
          {filtered ? t('databases.history.emptyFiltered') : t('databases.history.empty')}
        </p>
      )}
      {events.length > 0 && (
        <ol className={styles.list} data-testid="history-list">
          {groupByOperation(events).map(group)}
        </ol>
      )}
      {query.hasNextPage && (
        <Button
          variant="secondary"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage
            ? t('databases.history.loadingMore')
            : t('databases.history.more')}
        </Button>
      )}
      {bulk && (
        <HistoryDetailsDialog
          databaseId={databaseId}
          event={bulk.event}
          recordId={recordId}
          text={bulk.text}
          onClose={() => {
            setBulk(null);
          }}
        />
      )}
      {detail && (
        <HistoryValueDialog
          title={detail.title}
          line={detail.line}
          onClose={() => {
            setDetail(null);
          }}
        />
      )}
    </div>
  );
}

function Line({ line, onDetail }: { line: HistoryLine; onDetail: () => void }) {
  const { t } = useTranslation();
  const cut = (value: string) => (value.length > CUT ? `${value.slice(0, CUT)}…` : value);
  const changed = line.before !== undefined || line.after !== undefined;
  return (
    <div className={styles.line}>
      {line.label && <span className={styles.label}>{line.label}</span>}
      {changed ? (
        <span className={styles.change}>
          <span className="visually-hidden">{t('databases.history.before')}: </span>
          <span className={styles.was}>{cut(line.before ?? '')}</span>
          <span aria-hidden="true"> → </span>
          <span className="visually-hidden">{t('databases.history.after')}: </span>
          <span className={styles.now}>{cut(line.after ?? '')}</span>
        </span>
      ) : (
        <span>{cut(line.text ?? '')}</span>
      )}
      {line.long && (
        <button type="button" className={styles.more} onClick={onDetail}>
          {t('databases.history.details')}
        </button>
      )}
    </div>
  );
}

/** A bulk entry: its changes sit in stored chunks, read on demand. */
const chunked = (event: HistoryEvent) => Number(event.payload.chunks ?? 0) > 0;
const totalOf = (event: HistoryEvent) => Number(event.payload.changed ?? event.payload.count ?? 0);
